import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Badge,
  Button,
  FeaturedIcon,
  PageStatus,
  SkeletonLoader,
} from '@unified-checkout/design-system';
import {
  ArrowRight,
  CircleExclamation,
  CircleInfo,
  Download,
  File,
  Lock,
} from '@unified-checkout/design-system/icons/index';

import type { IReportsResponse } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/reports';
import type { IActiveBalance } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance';
import { notify } from '@app/dashboard/utils/notifier';
import { useLangStore } from '@app/dashboard/store/lang';
import { useCancelQuery } from '@app/dashboard/hooks/useCancelQuery';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
  BALANCE_TRANSFERS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  downloadBalanceReport,
  getBalanceReports,
} from '@app/dashboard/services/inner_layout/balance_and_transfers/balance';
import { downloadFile } from '@app/dashboard/utils/downloadFile';
import { formatToISODate } from '@app/dashboard/utils/dateFormats';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';

import { handleStoreLastAction } from '../../../../utils/events-tracking';

import styles from './reports.module.scss';

const Reports = ({
  activeBalance,
}: {
  activeBalance: IActiveBalance | null;
}) => {
  const navigate = useNavigate();
  const clickedReportRef = useRef<number | null>(null);
  const hasDownloadPerms = isAccessible(PERMISSIONS.CAN_DOWNLOAD_REPORTS);

  const { lang } = useLangStore();
  const { t } = useTranslation();

  const { data, isFetching, isError } = useQuery({
    queryKey: [QUERY_KEYS.GET_BALANCE_REPORTS, lang],
    queryFn: ({ signal }) => getBalanceReports(signal),
    select: (data) => data.data?.data as IReportsResponse,
    enabled: hasDownloadPerms,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (reportId: number) => downloadBalanceReport(reportId),
    onSuccess: (downloadResponse) => {
      const date = formatToISODate(new Date());
      const fileName = `report-${clickedReportRef.current}-${date}.xlsx`;
      const eventName =
        BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.TRANSFER_REPORTS_DOWNLOADED;

      amplitude.track(eventName, {
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.REPORT_ID]:
          clickedReportRef.current,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.REPORT_DATE_RANGE]:
          date,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.REPORT_TYPE]:
          BALANCE_TRANSFERS_EVENTS_VALUES.XLSX,
      });

      handleStoreLastAction(eventName);

      downloadFile({
        res: downloadResponse,
        defaultFilename: fileName,
      });
    },
    onError: (axiosErr: AxiosError<{ message?: string; detail?: string }>) => {
      let message = t('errors.somethingWentWrong');

      if (axiosErr.response?.data?.message) {
        message = axiosErr.response.data.message;
      } else if (axiosErr.message) {
        message = axiosErr.message;
      } else if (axiosErr.response?.data?.detail) {
        message = axiosErr.response.data.detail;
      }

      notify.error(message);
    },
  });

  const hasReports = !isError && !!data?.total_count;

  const activeReportsEmptyState = useMemo(() => {
    if (!hasDownloadPerms) {
      return {
        status: 'default' as const,
        title: t('innerLayout.balanceTransfers.balance.reports.noPremTitle'),
        description: t(
          'innerLayout.balanceTransfers.balance.reports.noPremSubTitle'
        ),
        icon: Lock,
      };
    }

    if (isError) {
      return {
        status: 'error' as const,
        description: t('innerLayout.balanceTransfers.balance.reports.error'),
        icon: CircleExclamation,
      };
    }

    if (!hasReports) {
      return {
        status: 'default' as const,
        description: t(
          'innerLayout.balanceTransfers.balance.reports.emptyState'
        ),
        icon: CircleInfo,
      };
    }

    return undefined;
  }, [hasDownloadPerms, isError, hasReports, t]);

  const handleDownloadReport = (reportId: number) => {
    clickedReportRef.current = reportId;
    mutate(reportId);
  };

  const handleViewAllReports = () => {
    const eventName =
      BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.TRANSFER_REPORTS_VIEW_ALL_CLICKED;
    amplitude.track(eventName, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.SELECTED_CURRENCY]:
        activeBalance?.currency,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.ACCOUNT_ID]:
        activeBalance?.account?.id,
    });
    handleStoreLastAction(eventName);

    navigate(INNER_ROUTES.REPORTS);
  };

  useCancelQuery({
    queryKey: [QUERY_KEYS.GET_BALANCE_REPORTS, lang],
    deps: [lang],
  });

  return (
    <div className={styles['reports']}>
      <div className={styles['reports__header']}>
        <div className={styles['reports__header-content']}>
          <p className={styles['reports__header-content-title']}>
            {t('innerLayout.balanceTransfers.balance.reports.title')}
          </p>
          {
            <p className={styles['reports__header-content-description']}>
              {t(
                'innerLayout.balanceTransfers.balance.reports.showingReports',
                {
                  count: data?.results.length || 0,
                  total: data?.total_count || 0,
                }
              )}
            </p>
          }
        </div>
        {isFetching ? (
          <SkeletonLoader
            width="50%"
            height="2.125rem"
            noOfRows={1}
            type="transparent_box"
          />
        ) : !hasDownloadPerms ? (
          <Badge
            color="gray"
            size="md"
            text={t('innerLayout.balanceTransfers.balance.reports.locked')}
          />
        ) : (
          hasReports && (
            <Button
              text={t('innerLayout.balanceTransfers.balance.reports.viewAll')}
              size="sm"
              variant="link"
              trailingIcon={ArrowRight}
              onClickHandler={handleViewAllReports}
              disabled={isPending || isFetching}
            />
          )
        )}
      </div>
      <div className={styles['reports__list']}>
        {isFetching ? (
          Array.from({ length: 3 }).map((_, index) => (
            <SkeletonLoader
              width="100%"
              height="4.125rem"
              noOfRows={1}
              type="bordered_box"
              key={index}
            />
          ))
        ) : activeReportsEmptyState ? (
          <PageStatus
            status={activeReportsEmptyState.status}
            title={activeReportsEmptyState.title}
            description={activeReportsEmptyState.description}
            iconSize="lg"
            icon={activeReportsEmptyState.icon}
            showBgCircles={false}
          />
        ) : (
          data?.results.map(({ date_range, id, title }) => (
            <div className={styles['reports__list-content']} key={id}>
              <FeaturedIcon icon={File} color="gray" size="sm" />
              <div className={styles['reports__list-content-details']}>
                <p className={styles['reports__list-content-details-title']}>
                  {title}
                </p>
                <p
                  className={
                    styles['reports__list-content-details-description']
                  }
                >
                  {date_range}
                </p>
              </div>
              <Button
                size="md"
                variant="link"
                color="secondary"
                trailingIcon={
                  isPending && clickedReportRef.current === id
                    ? undefined
                    : Download
                }
                onClickHandler={() => handleDownloadReport(id)}
                disabled={isPending}
                loading={isPending && clickedReportRef.current === id}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Reports;
